// ═══════════════════════════════════════════════════════════════
// ENGAGEMENT SCORING ENGINE
// Calculates member engagement from contact logs, gifts,
// event attendance, and show sponsorships — all with linear
// time decay (full value → zero over 24 months).
//
// v2.0.0 — Multi-source scoring:
//   - Each gift scores independently (20 pts) from gifts table
//   - Event attendance scores 10 pts per event
//   - Show sponsorships score 25 pts each
//   - Legacy fallback: member.lastGiftDate if no gift array
//
// v1.6.0 — Score Audit breakdown:
//   - New `breakdown` array in return object captures EVERY contact
//     log entry with a `status` explaining its score contribution.
//   - Existing `scoredContacts` array unchanged (backward compatible).
//
// v1.5.2 — Direction-aware scoring:
//   - Outbound contacts (direction: "outbound") record activity but score 0 pts
//   - Inbound contacts (direction: "inbound" or undefined) score normally
//   - Types without direction field (legacy logs) score normally (backward compatible)
//
// Usage:
//   import { calculateEngagement, getPathwayForScore, DEFAULT_SCORING_CONFIG } from "../utils/engagementScoring";
//   const engagement = calculateEngagement(member, scoringConfig);
//   const pathway = getPathwayForScore(engagement.score, scoringConfig);
//   // engagement.breakdown → full audit trail of every entry
// ═══════════════════════════════════════════════════════════════

// ── Default Scoring Configuration ─────────────────────────────
// Stored in Supabase app_settings; editable in Settings → Scoring
export const DEFAULT_SCORING_CONFIG = {
  // Points per contact type
  contactPoints: {
    email: 2.5,
    phone: 2.5,
    call: 2.5, // alias for phone
    text: 2.5,
    coffee: 10,
    lunch: 10,
    meeting: 10, // treated same as coffee/lunch
    donation: 20,
    gift: 20, // alias for donation
    volunteering: 15,
    event: 1.5,
    note: 0, // notes are records, not engagement signals
  },

  // Points for activity sources beyond contact logs
  activityPoints: {
    gift: 20,              // per gift recorded in gifts table
    eventAttendance: 10,   // per event attended
    showSponsorship: 25,   // per show sponsorship
  },

  // Pathway thresholds (score required to enter each pathway)
  pathways: {
    cold: { min: 0, max: 9.99, label: "New Donor" },
    connect: { min: 10, max: 29.99, label: "Donor of Record" },
    contribute: { min: 30, max: 79.99, label: "Engagement" },
    lead: { min: 80, max: 150, label: "Advocate" },
  },

  // Decay: contacts lose all value after this many months
  decayMonths: 24,

  // Maximum score cap
  maxScore: 150,
};

// ── Core Scoring Function ─────────────────────────────────────
// Each contact log entry earns points based on type.
// Points decay linearly from full value to zero over decayMonths.
// Score = sum of all decayed contact points, capped at maxScore.
//
// Direction rule:
//   - "outbound" → 0 points (you reached out, doesn't prove engagement)
//   - "inbound" or undefined → full points (they responded or initiated)
//   - Outbound contacts STILL count for "days since last contact" recency
//
// Breakdown statuses:
//   - "scoring"   → inbound, within decay window, earns points
//   - "outbound"  → logged activity, 0 pts (manager effort, not member engagement)
//   - "expired"   → older than decay window, 0 pts
//   - "zero-type" → type has 0 base points (e.g., note), 0 pts
export const calculateEngagement = (
  member,
  config = DEFAULT_SCORING_CONFIG,
  { memberGifts = [], memberEvents = [], memberSponsorships = [] } = {}
) => {
  const logs = member.contactLog || [];
  const now = Date.now();
  const decayMs = (config.decayMonths || 24) * 30.44 * 24 * 60 * 60 * 1000; // months → ms

  let rawScore = 0;
  const scoredContacts = [];
  const breakdown = [];

  logs.forEach((log) => {
    const logDate = new Date(log.date || log.createdAt);
    const ageMs = now - logDate.getTime();
    const contactType = (log.type || "note").toLowerCase();
    const direction = log.direction || "inbound";
    const isOutbound = direction === "outbound";
    const basePoints = config.contactPoints[contactType] ?? 0;

    // ── Expired: older than decay window ─────────────────────
    if (ageMs >= decayMs) {
      breakdown.push({
        type: contactType,
        direction: direction,
        date: log.date || log.createdAt,
        basePoints: basePoints,
        decayFactor: 0,
        decayedPoints: 0,
        status: "expired",
        note: log.notes || null,
      });
      return;
    }

    const decayFactor = Math.max(0, 1 - ageMs / decayMs);

    // ── Outbound: recorded but doesn't earn points ───────────
    if (isOutbound) {
      breakdown.push({
        type: contactType,
        direction: direction,
        date: log.date || log.createdAt,
        basePoints: basePoints,
        decayFactor: Math.round(decayFactor * 100) / 100,
        decayedPoints: 0,
        status: "outbound",
        note: log.notes || null,
      });
      return;
    }

    // ── Zero-type: type worth 0 points (e.g., note) ─────────
    if (basePoints === 0) {
      breakdown.push({
        type: contactType,
        direction: direction,
        date: log.date || log.createdAt,
        basePoints: 0,
        decayFactor: Math.round(decayFactor * 100) / 100,
        decayedPoints: 0,
        status: "zero-type",
        note: log.notes || null,
      });
      return;
    }

    // ── Scoring: inbound, within window, has points ──────────
    const decayedPoints = basePoints * decayFactor;
    rawScore += decayedPoints;

    const entry = {
      type: contactType,
      direction: direction,
      date: log.date || log.createdAt,
      basePoints: basePoints,
      decayFactor: Math.round(decayFactor * 100) / 100,
      decayedPoints: Math.round(decayedPoints * 10) / 10,
      status: "scoring",
      note: log.notes || null,
    };

    scoredContacts.push(entry);
    breakdown.push(entry);
  });

  // ── Gift scoring ─────────────────────────────────────────────
  // Each gift in the gifts table scores independently with decay.
  // Falls back to legacy member.lastGiftDate if no gift array provided.
  const giftBasePoints = config.activityPoints?.gift ?? config.contactPoints.donation ?? 20;
  if (memberGifts.length > 0) {
    memberGifts.forEach((gift) => {
      const giftDate = new Date((gift.gift_date || gift.giftDate) + "T12:00:00");
      const giftAgeMs = now - giftDate.getTime();
      if (giftAgeMs >= decayMs) {
        breakdown.push({
          type: "gift", direction: "inbound",
          date: gift.gift_date || gift.giftDate,
          basePoints: giftBasePoints, decayFactor: 0, decayedPoints: 0,
          status: "expired", note: "Gift",
        });
        return;
      }
      const decay = Math.max(0, 1 - giftAgeMs / decayMs);
      const pts = giftBasePoints * decay;
      rawScore += pts;
      const entry = {
        type: "gift", direction: "inbound",
        date: gift.gift_date || gift.giftDate,
        basePoints: giftBasePoints,
        decayFactor: Math.round(decay * 100) / 100,
        decayedPoints: Math.round(pts * 10) / 10,
        status: "scoring", note: "Gift",
      };
      scoredContacts.push(entry);
      breakdown.push(entry);
    });
  } else if (member.giftCount > 0 && member.lastGiftDate) {
    // Legacy fallback: single gift signal from member record
    const giftDate = new Date(member.lastGiftDate);
    const giftAgeMs = now - giftDate.getTime();
    const decay = giftAgeMs < decayMs ? Math.max(0, 1 - giftAgeMs / decayMs) : 0;
    const pts = decay * giftBasePoints;
    if (decay > 0) rawScore += pts;
    const entry = {
      type: "gift", direction: "inbound", date: member.lastGiftDate,
      basePoints: giftBasePoints,
      decayFactor: Math.round(decay * 100) / 100,
      decayedPoints: Math.round(pts * 10) / 10,
      status: decay > 0 ? "scoring" : "expired",
      note: "Gift (legacy signal)",
    };
    if (decay > 0) scoredContacts.push(entry);
    breakdown.push(entry);
  }

  // ── Event attendance scoring ────────────────────────────────
  const eventBasePoints = config.activityPoints?.eventAttendance ?? 10;
  memberEvents.forEach((evt) => {
    const evtDate = new Date((evt.event_date || evt.eventDate) + "T12:00:00");
    const evtAgeMs = now - evtDate.getTime();
    if (evtAgeMs >= decayMs) {
      breakdown.push({
        type: "event_attendance", direction: "inbound",
        date: evt.event_date || evt.eventDate,
        basePoints: eventBasePoints, decayFactor: 0, decayedPoints: 0,
        status: "expired", note: "Event attended",
      });
      return;
    }
    const decay = Math.max(0, 1 - evtAgeMs / decayMs);
    const pts = eventBasePoints * decay;
    rawScore += pts;
    const entry = {
      type: "event_attendance", direction: "inbound",
      date: evt.event_date || evt.eventDate,
      basePoints: eventBasePoints,
      decayFactor: Math.round(decay * 100) / 100,
      decayedPoints: Math.round(pts * 10) / 10,
      status: "scoring", note: "Event attended",
    };
    scoredContacts.push(entry);
    breakdown.push(entry);
  });

  // ── Show sponsorship scoring ────────────────────────────────
  const sponsorBasePoints = config.activityPoints?.showSponsorship ?? 25;
  memberSponsorships.forEach((sp) => {
    const spDate = new Date((sp.date || sp.created_at) + "T12:00:00");
    const spAgeMs = now - spDate.getTime();
    if (spAgeMs >= decayMs) {
      breakdown.push({
        type: "show_sponsorship", direction: "inbound",
        date: sp.date || sp.created_at,
        basePoints: sponsorBasePoints, decayFactor: 0, decayedPoints: 0,
        status: "expired", note: "Show sponsorship",
      });
      return;
    }
    const decay = Math.max(0, 1 - spAgeMs / decayMs);
    const pts = sponsorBasePoints * decay;
    rawScore += pts;
    const entry = {
      type: "show_sponsorship", direction: "inbound",
      date: sp.date || sp.created_at,
      basePoints: sponsorBasePoints,
      decayFactor: Math.round(decay * 100) / 100,
      decayedPoints: Math.round(pts * 10) / 10,
      status: "scoring", note: "Show sponsorship",
    };
    scoredContacts.push(entry);
    breakdown.push(entry);
  });

  // Cap the score
  const score = Math.min(
    Math.round(rawScore * 10) / 10,
    config.maxScore || 150
  );

  // Days since last contact (counts ALL contacts, including outbound)
  let daysSinceContact = null;
  if (logs.length > 0) {
    const mostRecent = logs.reduce((latest, l) => {
      const d = new Date(l.date || l.createdAt);
      return d > latest ? d : latest;
    }, new Date(0));
    daysSinceContact = Math.floor(
      (now - mostRecent.getTime()) / (1000 * 60 * 60 * 24)
    );
  }

  return {
    score,
    daysSinceContact,
    totalContacts: logs.length,
    activeContacts: scoredContacts.length, // inbound contacts still within decay window
    scoredContacts, // backward compatible — only scoring entries
    breakdown, // NEW — full audit trail with status for every entry
  };
};

// ── Activity Data Helper ──────────────────────────────────────
// Filters centralized arrays for a specific member.
// Use with AppContext's allGifts, allEventAttendance, allSponsorships.
export const getMemberActivities = (memberId, allGifts = [], allEventAttendance = [], allSponsorships = []) => ({
  memberGifts: allGifts.filter((g) => g.member_id === memberId),
  memberEvents: allEventAttendance.filter((e) => e.member_id === memberId),
  memberSponsorships: allSponsorships.filter((s) => s.member_id === memberId),
});

// ── Pathway Resolver ──────────────────────────────────────────
// Given a score, returns the pathway key and label.
// NOTE: This NEVER returns "prospect" — prospect is a manually-assigned
// status, not a score-based pathway.
export const getPathwayForScore = (score, config = DEFAULT_SCORING_CONFIG) => {
  const pathways = config.pathways || DEFAULT_SCORING_CONFIG.pathways;

  if (score >= pathways.lead.min)
    return { key: "lead", label: pathways.lead.label };
  if (score >= pathways.contribute.min)
    return { key: "contribute", label: pathways.contribute.label };
  if (score >= pathways.connect.min)
    return { key: "connect", label: pathways.connect.label };
  return { key: "cold", label: pathways.cold.label };
};

// ── Stage config for pipeline columns ─────────────────────────
// Generates PIPELINE_STAGES array from scoring config.
// NOTE: Does NOT include "prospect" — prospect is handled separately
// by the CultivationView which reads from app pathways.
export const getPipelineStages = (config = DEFAULT_SCORING_CONFIG) => {
  const pw = config.pathways || DEFAULT_SCORING_CONFIG.pathways;
  return [
    {
      key: "cold",
      label: pw.cold.label,
      scoreRange: [pw.cold.min, pw.cold.max],
      instruction: "Not yet engaged — make first contact",
    },
    {
      key: "connect",
      label: pw.connect.label,
      scoreRange: [pw.connect.min, pw.connect.max],
      instruction: "Building relationship — stay in touch",
    },
    {
      key: "contribute",
      label: pw.contribute.label,
      scoreRange: [pw.contribute.min, pw.contribute.max],
      instruction: "Giving time or money — deepen involvement",
    },
    {
      key: "lead",
      label: pw.lead.label,
      scoreRange: [pw.lead.min, pw.lead.max],
      instruction: "Champion — steward and celebrate",
    },
  ];
};

// ── Get stage for a specific score ────────────────────────────
export const getStageForScore = (score, config = DEFAULT_SCORING_CONFIG) => {
  const stages = getPipelineStages(config);
  return (
    stages.find((s) => score >= s.scoreRange[0] && score <= s.scoreRange[1]) ||
    stages[0]
  );
};

// ── Effective Pathway Resolver ────────────────────────────────
// Combines calculated pathway with manual override.
//
// Rules:
//   1. If pathway === "prospect" → always return prospect (it's a status, not a score)
//   2. If pathway is manually set to something other than "unengaged" → override wins
//   3. Otherwise → calculated pathway from scoring engine

const SCORING_PATHWAY_COLORS = {
  prospect: "#b91c1c",
  cold: "#9ca3af",
  connect: "#3b82f6",
  contribute: "#10b981",
  lead: "#f59e0b",
};

// ── Portfolio Pathway Floor ───────────────────────────────────
// Pathway hierarchy for floor comparisons (higher index = higher pathway)
const PATHWAY_RANK = { unengaged: 0, cold: 0, connect: 1, contribute: 2, lead: 3 };

const applyPortfolioFloor = (calculatedKey, member, lifetimeGiving = 0) => {
  if (!member.assignedDirector) return calculatedKey;
  // Portfolio donors: floor is "contribute" (Engagement)
  // Lifetime giving > $10,000: floor is "lead" (Advocate)
  const floor = lifetimeGiving > 10000 ? "lead" : "contribute";
  const calcRank = PATHWAY_RANK[calculatedKey] ?? 0;
  const floorRank = PATHWAY_RANK[floor] ?? 0;
  return calcRank >= floorRank ? calculatedKey : floor;
};

export const resolveEffectivePathway = (
  member,
  score,
  appPathways = [],
  config = DEFAULT_SCORING_CONFIG,
  { lifetimeGiving = 0 } = {}
) => {
  const calculated = getPathwayForScore(score, config);
  const manualId = member.pathway;

  // ── Rule 1: Prospect always wins ──────────────────────────
  if (manualId === "prospect") {
    const prospectPathway = appPathways.find((p) => p.id === "prospect");
    return {
      key: "prospect",
      label: prospectPathway?.name || "Prospect",
      color: prospectPathway?.color || SCORING_PATHWAY_COLORS.prospect,
      source: "prospect",
      mismatch: false,
      calculatedKey: calculated.key === "cold" ? "unengaged" : calculated.key,
      calculatedLabel: calculated.label,
      calculatedColor: SCORING_PATHWAY_COLORS[calculated.key] || "#9ca3af",
    };
  }

  // Map scoring engine keys to the app's pathway system
  const scoringToAppKey = (key) => (key === "cold" ? "unengaged" : key);

  const calculatedAppKey = scoringToAppKey(calculated.key);

  // ── Rule 1b: Portfolio floor ──────────────────────────────
  // Portfolio donors (assigned to a director) get a minimum pathway:
  //   - All portfolio donors: at least "contribute" (Engagement)
  //   - Lifetime giving > $10,000: at least "lead" (Advocate)
  // If removed from portfolio, reverts to scoring engine calculation.
  const flooredAppKey = applyPortfolioFloor(calculatedAppKey, member, lifetimeGiving);

  const flooredPathway =
    appPathways.find((p) => p.id === flooredAppKey) || null;
  const calculatedPathway =
    appPathways.find((p) => p.id === calculatedAppKey) || null;
  const manualPathway = appPathways.find((p) => p.id === manualId) || null;

  // ── Rule 2: Manual override wins if explicitly set ────────
  const hasManualOverride =
    manualId &&
    manualId !== "unengaged" &&
    manualId !== "prospect" &&
    manualId !== flooredAppKey;

  if (hasManualOverride && manualPathway) {
    return {
      key: manualId,
      label: manualPathway.name,
      color: manualPathway.color,
      source: "override",
      mismatch: true,
      calculatedKey: flooredAppKey,
      calculatedLabel: flooredPathway?.name || calculated.label,
      calculatedColor: SCORING_PATHWAY_COLORS[flooredAppKey] || SCORING_PATHWAY_COLORS[calculated.key] || "#9ca3af",
    };
  }

  // ── Rule 3: Calculated pathway (with portfolio floor) wins ─
  const isPortfolioFloored = flooredAppKey !== calculatedAppKey;
  return {
    key: flooredAppKey,
    label: flooredPathway?.name || calculated.label,
    color:
      flooredPathway?.color ||
      calculatedPathway?.color ||
      SCORING_PATHWAY_COLORS[calculated.key] ||
      "#9ca3af",
    source: isPortfolioFloored ? "portfolio" : "calculated",
    mismatch: isPortfolioFloored,
    calculatedKey: calculatedAppKey,
    calculatedLabel: calculated.label,
    calculatedColor: SCORING_PATHWAY_COLORS[calculated.key] || "#9ca3af",
  };
};
